import { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardHeader, 
  Spinner, 
  TableColumn
} from '@heroui/react';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableCell 
} from '@heroui/react';
import { 
  Pagination 
} from '@heroui/react';
import { 
  Dropdown, 
  DropdownTrigger, 
  DropdownMenu, 
  DropdownItem 
} from '@heroui/react';
import { Button } from '@heroui/react';
import { Input } from '@heroui/react';
import { 
  Select, 
  SelectItem 
} from '@heroui/react';
import MarcaService from '@/services/marca/marca.service';
import type { Marca, MarcaRequest } from '@/types/marca.model';
import MarcaModal from '@/components/marca/newModal';
import MarcaEditModal from '@/components/marca/editModal';
import MarcaDetailModal from '@/components/marca/detailModal';
import DeleteMarcaModal from '@/components/marca/deleteModal';

const MarcaPage = () => {
  const [marcas, setMarcas] = useState<Marca[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentMarca, setCurrentMarca] = useState<Marca | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Filtering and sorting state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('nombre');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Fetch marcas
  useEffect(() => {
    const fetchMarcas = async () => {
      try {
        setLoading(true);
        const response = await MarcaService.getAll({
          page: currentPage,
          limit: itemsPerPage,
          filter: searchTerm,
          sortBy,
          sortOrder
        });
        
        setMarcas(response.data);
        setTotalItems(response.meta.pagination.total);
        setTotalPages(response.meta.pagination.pageCount);
        setError(null);
      } catch (err) {
        setError('Error al cargar las marcas');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchMarcas();
    return () => { setMarcas([]); }
  }, [currentPage, itemsPerPage, searchTerm, sortBy, sortOrder]);

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value: string) => {
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      setItemsPerPage(numValue);
      setCurrentPage(1); // Reset to first page when changing items per page
    }
  };

  // Handle sort change
  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle dropdown actions
  const handleEdit = (marca: Marca) => {
    setCurrentMarca(marca);
    setIsEditModalOpen(true);
  };

  const handleDelete = (marca: Marca) => {
    setCurrentMarca(marca);
    setIsDeleteModalOpen(true);
  };

  const handleViewDetail = (marca: Marca) => {
    setCurrentMarca(marca);
    setIsDetailModalOpen(true);
  };

  // Handle deleting a marca
  const handleDeleteMarca = async (id: string | number) => {
    setIsSubmitting(true);
    try {
      // Call the service to delete the marca
      await MarcaService.delete(id);
      
      // Refresh the list of marcas
      const response = await MarcaService.getAll({
        page: currentPage,
        limit: itemsPerPage,
        filter: searchTerm,
        sortBy,
        sortOrder
      });
      
      setMarcas(response.data);
      setTotalItems(response.meta.pagination.total);
      setTotalPages(response.meta.pagination.pageCount);
    } catch (err) {
      setError('Error al eliminar la marca');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle modals
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen);
    if (!isEditModalOpen) {
      // When opening the edit modal, make sure we have currentMarca
    } else {
      // When closing the edit modal, clear the current marca
      setCurrentMarca(undefined);
    }
  };

  const toggleDetailModal = () => {
    setIsDetailModalOpen(!isDetailModalOpen);
    if (!isDetailModalOpen) {
      // When opening the detail modal, make sure we have currentMarca
    } else {
      // When closing the detail modal, clear the current marca
      setCurrentMarca(undefined);
    }
  };

  const toggleDeleteModal = () => {
    setIsDeleteModalOpen(!isDeleteModalOpen);
    if (!isDeleteModalOpen) {
      // When opening the delete modal, make sure we have currentMarca
    } else {
      // When closing the delete modal, clear the current marca
      setCurrentMarca(undefined);
    }
  };

  // Calculate sort indicator
  const getSortIndicator = (field: string) => {
    if (sortBy !== field) return '';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Handle creating a new marca
  const handleCreateMarca = async (data: Omit<MarcaRequest['data'], 'tenantId' | 'locale' | 'localizations'>) => {
    setIsSubmitting(true);
    try {
      // Prepare the data for submission
      const marcaData: MarcaRequest['data'] = {
        ...data
      };

      // Call the service to create the marca
      await MarcaService.create({ data: marcaData });
      
      // Refresh the list of marcas
      const response = await MarcaService.getAll({
        page: currentPage,
        limit: itemsPerPage,
        filter: searchTerm,
        sortBy,
        sortOrder
      });
      
      setMarcas(response.data);
      setTotalItems(response.meta.pagination.total);
      setTotalPages(response.meta.pagination.pageCount);
    } catch (err) {
      setError('Error al crear la marca');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle updating a marca
  const handleUpdateMarca = async (id: string | number, data: Omit<MarcaRequest['data'], 'tenantId' | 'locale' | 'localizations'>) => {
    setIsSubmitting(true);
    try {
      // Prepare the data for submission
      const marcaData: MarcaRequest['data'] = {
        ...data
      };

      // Call the service to update the marca
      await MarcaService.update(id, { data: marcaData });
      
      // Refresh the list of marcas
      const response = await MarcaService.getAll({
        page: currentPage,
        limit: itemsPerPage,
        filter: searchTerm,
        sortBy,
        sortOrder
      });
      
      setMarcas(response.data);
      setTotalItems(response.meta.pagination.total);
      setTotalPages(response.meta.pagination.pageCount);
    } catch (err) {
      setError('Error al actualizar la marca');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto py-10 px-4">
      <Card className="w-full">
        <CardHeader className="border-b flex flex-direction-row justify-between items-center p-6">
          <h3 className="text-2xl font-bold">Gestión de Marcas</h3>          
          <p className="text-default-500">Administra las marcas de tu sistema</p>
        </CardHeader>
        <CardBody className="p-6">
          {/* Filters and Controls */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              <div className="relative w-full md:w-64">
                <Input
                  placeholder="Buscar marcas..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-10"
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              
              <Select 
                aria-label="Elementos por página"
                className="w-[180px]"
                selectedKeys={[itemsPerPage.toString()]}
                onSelectionChange={(keys) => {
                  const value = Array.from(keys).join('');
                  if (value) handleItemsPerPageChange(value);
                }}
              >
                <SelectItem key="5">5 por página</SelectItem>
                <SelectItem key="10">10 por página</SelectItem>
                <SelectItem key="20">20 por página</SelectItem>
                <SelectItem key="50">50 por página</SelectItem>
              </Select>
            </div>
            
            <Button 
              className="w-full md:w-auto"
              onPress={toggleModal}
            >
              <svg
                className="mr-2 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Nueva Marca
            </Button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center text-red-500">
                <p>{error}</p>
                <Button 
                  variant="bordered" 
                  onPress={() => window.location.reload()}
                  className="mt-4"
                >
                  Reintentar
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableColumn key="acciones" className="px-3 py-2 text-left">Acciones</TableColumn>
                    <TableColumn 
                      key="nombre"
                      onClick={() => handleSortChange('nombre')}
                      allowsSorting
                    >
                      Nombre {getSortIndicator('nombre')}
                    </TableColumn>
                    <TableColumn key="descripcion">Descripción</TableColumn>
                </TableHeader>
                <TableBody
                  isLoading={loading}
                  loadingContent={<Spinner label="Loading..." />}
                  items={marcas}
                >
                  {marcas.length > 0 ? (
                    marcas.map((marca) => (
                      <TableRow key={marca.id}>
                                                <TableCell className="text-left">
                          <Dropdown>
                            <DropdownTrigger>
                              <Button variant="light" className="h-8 w-8 p-0">
                                <span className="sr-only">Abrir menú</span>
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                                  />
                                </svg>
                              </Button>
                            </DropdownTrigger>
                            <DropdownMenu aria-label="Acciones de la marca">
                              <DropdownItem key="edit" onClick={() => marca.documentId !== undefined && handleEdit(marca)}>
                                <svg
                                  className="mr-2 h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                                Editar
                              </DropdownItem>
                              <DropdownItem key="view" onClick={() => marca.documentId !== undefined && handleViewDetail(marca)}>
                                <svg
                                  className="mr-2 h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                                Ver detalle
                              </DropdownItem>
                              <DropdownItem 
                                key="delete" 
                                className="text-danger"
                                color="danger"
                                onClick={() => marca.documentId !== undefined && handleDelete(marca)}
                              >
                                <svg
                                  className="mr-2 h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                                Eliminar
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </TableCell>
                        <TableCell className="font-medium">{marca.nombre}</TableCell>
                        <TableCell>{marca.descripcion || '-'}</TableCell>

                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-10">
                        No se encontraron marcas
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && !loading && (
            <div className="flex flex-col sm:flex-row items-center justify-between pt-6">
              <div className="text-sm text-default-500 mb-4 sm:mb-0">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} resultados
              </div>
              <Pagination
                total={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                showControls
              />
            </div>
          )}
        </CardBody>
      </Card>
      
      {/* Marca Modal */}
      <MarcaModal 
        isOpen={isModalOpen} 
        onClose={toggleModal} 
        onSubmit={handleCreateMarca}
        loading={isSubmitting}
      />
      
      {/* Marca Edit Modal */}
      <MarcaEditModal 
        isOpen={isEditModalOpen} 
        onClose={toggleEditModal} 
        onSubmit={handleUpdateMarca}
        loading={isSubmitting}
        marca={currentMarca}
      />
      
      {/* Marca Detail Modal */}
      <MarcaDetailModal 
        isOpen={isDetailModalOpen} 
        onClose={toggleDetailModal}
        marca={currentMarca}
      />
      
      {/* Marca Delete Modal */}
      <DeleteMarcaModal 
        isOpen={isDeleteModalOpen} 
        onClose={toggleDeleteModal}
        onDelete={handleDeleteMarca}
        marca={currentMarca}
      />
    </div>
  );
};

export default MarcaPage;